import type { IntencionMensaje, IntencionStatus } from '@/lib/types'

/**
 * Qué pide el mensaje, puesto por el triador.
 *
 * En reposo es una etiqueta chica en el pie de la burbuja, al lado de la hora: un dato
 * más, no una alarma. Mismo criterio que `TranscripcionAudio`.
 *
 * No se muestra para `social`, `ruido` ni cuando quedó `omitida`/`fallida`: etiquetar un
 * «gracias crack» como «social» es ruido visual, y la gracia del chip es que cuando
 * aparece, signifique algo.
 */

const ETIQUETAS: Record<IntencionMensaje, string> = {
  pedido: 'Pedido',
  bug: 'Bug',
  estado: 'Estado',
  comercial: 'Comercial',
  administrativo: 'Admin',
  operacion: 'Operación',
  social: 'Social',
  ruido: 'Ruido',
}

const TITULOS: Record<IntencionMensaje, string> = {
  pedido: 'Pide algo nuevo',
  bug: 'Reporta algo roto',
  estado: 'Pregunta por un avance',
  comercial: 'Precio, cotización o propuesta',
  administrativo: 'Pago, factura o acceso',
  operacion: 'Día a día del cliente',
  social: 'Saludo o charla',
  ruido: 'Automático o promoción',
}

/** Las que no vale la pena mostrar: no piden nada del equipo. */
const OCULTAS: IntencionMensaje[] = ['social', 'ruido']

interface Props {
  intencion: IntencionMensaje | null
  status: IntencionStatus | null
  /** Debajo de esto la etiqueta se marca como dudosa en vez de afirmarse. */
  confianza?: number | null
}

export default function IntencionChip({ intencion, status, confianza }: Props) {
  if (status !== 'ok' || !intencion) return null
  if (OCULTAS.includes(intencion)) return null

  const dudosa = typeof confianza === 'number' && confianza < 0.6
  const titulo = dudosa
    ? `${TITULOS[intencion]} — clasificación dudosa`
    : TITULOS[intencion]

  return (
    <span
      className={`chat-intencion chat-intencion--${intencion}${dudosa ? ' chat-intencion--dudosa' : ''}`}
      title={titulo}
    >
      {ETIQUETAS[intencion]}
    </span>
  )
}
